import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PlanDialogResult, PlanEntry } from './add-plan-dialog/add-plan-dialog.component';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { URLPaths } from '../../common/utilities/urlPaths.config';
import { ApiheaderService } from '../../common/services/apiheader.service';

export type ChargedFilterValue = 'All' | 'Charged' | 'Uncharged';
export interface BatchPlanEntryPayload {
  entryDate: string;
  hours: number;
  charge: string;
  client: string;
  project: string;
  employee: string;
  task: string;
  rate: number;
  loginname: string;          // must be non-empty and identical for every entry
  entryOwner: string;
  time_id: number;
  invoice_date: string;
  currency_code: string;
  task_id: number;
  plan_version_id: number;
  IsClash: boolean;
  company_id: number;
  checkforclashes: boolean;
  PartialRejection: number;
}

export interface BatchEntryResult {
  Index: number;           // zero-based index into the request's entries array
  ResultCode: number;      // 0 = saved, -1 = saved as clash, anything else = failed
  Message: string;
}

export interface BatchSaveResponse {
  Title: string;
  Message: string;
  Results: BatchEntryResult[];
}

@Injectable({
  providedIn: 'root'
})
export class TimeEntryForPlanningService {

  private showClashesSubject = new BehaviorSubject<boolean>(true);
  showClashes$ = this.showClashesSubject.asObservable();

  private showNonClashedSubject = new BehaviorSubject<boolean>(true);
  showNonClashed$ = this.showNonClashedSubject.asObservable();

  private chargedFilterSubject = new BehaviorSubject<ChargedFilterValue>('All');
  chargedFilter$ = this.chargedFilterSubject.asObservable();

  private clientFilterSubject = new BehaviorSubject<string[]>([]);
  clientFilter$ = this.clientFilterSubject.asObservable();

  private projectFilterSubject = new BehaviorSubject<string[]>([]);
  projectFilter$ = this.projectFilterSubject.asObservable();

  private employeeFilterSubject = new BehaviorSubject<string[]>([]);
  employeeFilter$ = this.employeeFilterSubject.asObservable();

  private dateRangeSubject = new BehaviorSubject<{ start: string | null; end: string | null }>({ start: null, end: null });
  dateRange$ = this.dateRangeSubject.asObservable();


  constructor(
    private _http: HttpClient
  ) { }

  setShowClashes(value: boolean): void {
    this.showClashesSubject.next(value);
  }

  setShowNonClashed(value: boolean): void {
    this.showNonClashedSubject.next(value);
  }

  setChargedFilter(value: ChargedFilterValue): void {
    this.chargedFilterSubject.next(value || 'All');
  }

  setClientFilter(value: string[]): void {
    this.clientFilterSubject.next(value || []);
  }

  setProjectFilter(value: string[]): void {
    this.projectFilterSubject.next(value || []);
  }

  setEmployeeFilter(value: string[]): void {
    this.employeeFilterSubject.next(value || []);
  }

  setDateRange(start: string | null, end: string | null): void {
    this.dateRangeSubject.next({ start, end });
  }

  // Returns the highest existing id + 1, used to assign ids to new
  // client-side entries before they're saved to the server
  private getNextId(entries: PlanEntry[]): number {
    const maxId = entries.reduce((max, e) => (e.id > max ? e.id : max), 0);
    return maxId + 1;
  }

  savePlanEntry(payload: any): Observable<any> {
    return this._http.post(
      URLPaths.URL_PLAN_ENTRY,
      payload,
      {
        headers: ApiheaderService.getHeaders()
      }
    );
  }

  savePlanEntriesBatch(entries: BatchPlanEntryPayload[]): Observable<BatchSaveResponse> {
  return this._http.post<BatchSaveResponse>(
    URLPaths.URL_PLAN_ENTRY_BATCH,
    { entries },
    { headers: ApiheaderService.getHeaders() }
  );
}

  clashKey(consultant: string, date: string): string {
    return `${consultant}|${date}`;
  }

  // Flags entries as causing a clash only within the given set of dates
  recomputeClashFlagsForDates(entries: PlanEntry[], dates: Set<string>): PlanEntry[] {
    if (dates.size === 0) { return entries; }

    return entries.map(e => {
      if (e.isPlaceholder || !dates.has(e.date)) { return e; }
      return { ...e, causedClash: e.isUnsaved ? false : !!e.isClashEntry };
    });
  }

  private reconcileSeries(
    oldEntries: PlanEntry[],
    newEntries: PlanEntry[]
  ): { toKeep: PlanEntry[]; toRemove: PlanEntry[]; toAdd: PlanEntry[] } {
    const oldByDate = new Map(oldEntries.map(e => [e.date, e]));
    const newDates = new Set(newEntries.map(e => e.date));

    const toKeep: PlanEntry[] = [];
    const toAdd: PlanEntry[] = [];

    for (const entry of newEntries) {
      const existing = oldByDate.get(entry.date);
      if (existing) {
        toKeep.push({ ...entry, id: existing.id });
      } else {
        toAdd.push(entry);
      }
    }

    const toRemove = oldEntries.filter(e => !newDates.has(e.date));

    return { toKeep, toRemove, toAdd };
  }

  getDateRange(): { start: string | null; end: string | null } {
    return this.dateRangeSubject.value;
  }

  getClashKeysFromEntries(entries: PlanEntry[]): Set<string> {
    const keys = new Set<string>();
    entries.forEach(entry => {
      if (!entry.isPlaceholder && entry.isClashEntry) {
        keys.add(this.clashKey(entry.consultant, entry.date));
      }
    });
    return keys;
  }

  getClashCausingEntryIds(entries: PlanEntry[]): Set<number> {
    const ids = new Set<number>();
    entries.forEach(entry => {
      if (!entry.isPlaceholder && entry.isClashEntry) {
        ids.add(entry.id);
      }
    });
    return ids;
  }

  // Merges the add/edit dialog's result into the current entry list.
  applyDialogResult(current: PlanEntry[], result: PlanDialogResult): {
    entries: PlanEntry[];
    warnings: string[];
  } {
    const warnings: string[] = [];
    let entries = [...current];
    const affectedDates = new Set<string>(result.entries.map(e => e.date));

    if (result.replacedSeriesId) {
      const oldSeriesEntries = entries.filter(e => e.seriesId === result.replacedSeriesId);
      oldSeriesEntries.forEach(e => affectedDates.add(e.date));

      const { toRemove, toAdd, toKeep } = this.reconcileSeries(oldSeriesEntries, result.entries);

      const removableIds = new Set(toRemove.map(e => e.id));
      entries = entries.filter(e => !removableIds.has(e.id));

      const keptIds = new Set(toKeep.map(e => e.id));
      entries = entries.filter(e => !keptIds.has(e.id));
      let nextId = this.getNextId(entries);
      const withIds = toAdd.map(e => ({ ...e, id: nextId++ }));

      entries = [...entries, ...toKeep, ...withIds];

    } else if (result.replacedEntryId !== undefined) {
      const replaced = entries.find(e => e.id === result.replacedEntryId);
      if (replaced) { affectedDates.add(replaced.date); }

      entries = entries.filter(e => e.id !== result.replacedEntryId);

      let nextId = this.getNextId(entries);
      const withIds = result.entries.map(e => ({ ...e, id: e.id ?? nextId++ }));
      entries = [...entries, ...withIds];

    } else {
      const edited = result.entries[0];
      const existingIndex = edited?.id !== undefined ? entries.findIndex(e => e.id === edited.id) : -1;

      if (existingIndex > -1) {
        affectedDates.add(entries[existingIndex].date);
        entries = entries.filter(e => e.id !== edited.id);
        entries = [...entries, edited];
      } else {
        let nextId = this.getNextId(entries);
        const withIds = result.entries.map(e => ({ ...e, id: e.id ?? nextId++ }));
        entries = [...entries, ...withIds];
      }
    }

    entries = this.recomputeClashFlagsForDates(entries, affectedDates);

    return { entries, warnings };
  }

  deleteEntries(current: PlanEntry[], idsToDelete: number[]): {
    entries: PlanEntry[];
    warnings: string[];
  } {
    const warnings: string[] = [];
    const idSet = new Set(idsToDelete);
    const affectedDates = new Set<string>(
      current.filter(e => idSet.has(e.id)).map(e => e.date)
    );

    let entries = current.filter(e => !idSet.has(e.id));
    entries = this.recomputeClashFlagsForDates(entries, affectedDates);

    return { entries, warnings };
  }
}